import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, ArrowLeft } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const inputCls =
  "w-full rounded-xl border border-stone_warm bg-white px-4 py-3 text-sm text-charcoal placeholder-charcoal/35 outline-none transition-[border-color,box-shadow] duration-300 focus:border-gold focus:ring-2 focus:ring-gold/20";

export default function ResetPassword() {
  const { token } = useParams();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail, "Could not reset the password."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-ivory px-6" data-testid="reset-password-page">
      <motion.div
        initial={{ opacity: 0, y: 28 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-md"
      >
        <div className="mb-10 text-center">
          <div className="mb-4 flex items-center justify-center gap-2.5">
            <span className="inline-block h-2 w-2 rotate-45 bg-gold" aria-hidden="true" />
            <span className="font-serif text-2xl font-semibold tracking-tight text-charcoal">Joseph Guild</span>
          </div>
          <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-charcoal/45">Guild office — password reset</p>
        </div>

        <div className="rounded-2xl border border-stone_warm bg-white p-8 shadow-[0_8px_30px_rgba(26,26,26,0.04)] sm:p-10">
          {done ? (
            <div className="text-center" data-testid="reset-success">
              <p className="font-serif text-2xl font-light text-charcoal">Password updated.</p>
              <p className="mt-3 text-sm text-charcoal/60">You can now sign in with your new password.</p>
              <Link
                to="/admin/login"
                className="mt-6 inline-block rounded-full bg-charcoal px-8 py-3.5 font-mono text-[11px] uppercase tracking-[0.22em] text-ivory transition-colors duration-300 hover:bg-gold"
                data-testid="reset-success-login-link"
              >
                Sign in
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="reset-password-form">
              <div>
                <label htmlFor="reset-password" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.24em] text-charcoal/55">
                  New password
                </label>
                <input id="reset-password" type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} placeholder="At least 8 characters" data-testid="reset-new-password-input" />
              </div>
              <div>
                <label htmlFor="reset-confirm" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.24em] text-charcoal/55">
                  Confirm new password
                </label>
                <input id="reset-confirm" type="password" required minLength={8} value={confirm} onChange={(e) => setConfirm(e.target.value)} className={inputCls} placeholder="Repeat the password" data-testid="reset-confirm-password-input" />
              </div>

              {error && (
                <p className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" data-testid="reset-error">
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={loading}
                className="inline-flex w-full items-center justify-center gap-3 rounded-full bg-charcoal px-8 py-4 font-mono text-[11px] uppercase tracking-[0.22em] text-ivory transition-colors duration-300 hover:bg-gold disabled:opacity-60"
                data-testid="reset-submit-button"
              >
                {loading && <Loader2 size={14} className="animate-spin" />}
                {loading ? "Updating…" : "Set new password"}
              </button>
            </form>
          )}
        </div>

        <Link
          to="/admin/login"
          className="mt-8 flex items-center justify-center gap-2 font-mono text-[10px] uppercase tracking-[0.24em] text-charcoal/45 transition-colors hover:text-gold-dark"
          data-testid="reset-back-login-link"
        >
          <ArrowLeft size={12} /> Back to sign in
        </Link>
      </motion.div>
    </div>
  );
}
